import { inject, Injectable } from '@angular/core';
import { License, ProductVariation } from '@app/core/models';
import { CartProductAdd, CommerceService, ViewOptions, ViewService } from '@makiwin/ngx-drupal8-rest';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, mergeMap, Observable, of, tap } from 'rxjs';
import { AuthSessionService } from '../auth-session/auth-session.service';
import { CrispChatService } from '../crisp-chat/crisp-chat.service';
import { ModalManagerService } from '../modal-manager/modal-manager.service';
import { NavDataService } from '../nav-data/nav-data.service';
import { RolesService } from '../roles/roles.service';
import { SubscriptionPlansService } from '../subscription-plans/subscription-plans.service';

/**
 * Represents the current subscription details for a user.
 *
 * @property product_id - The unique identifier of the subscribed product.
 * @property variation_id - The unique identifier of the product variation associated with the subscription.
 */
export interface CurrentSubscription {
  product_id: string;
  variation_id: string;
}

@Injectable({
  providedIn: 'root',
})
export class MembershipService {
  /**
   * Observable BehaviorSubject that holds the current subscription information.
   * Emits the latest value of the user's current subscription or `undefined` if not set.
   *
   * @readonly
   * @type {BehaviorSubject<CurrentSubscription | undefined>}
   */
  readonly currentSubscription$: BehaviorSubject<CurrentSubscription | undefined> = new BehaviorSubject<
    CurrentSubscription | undefined
  >(undefined);
  /**
   * A BehaviorSubject that holds the current state of the user's expired subscription.
   * Emits the latest value of `CurrentSubscription` if the subscription is expired, or `undefined` if not.
   * Can be used to observe changes in the expired subscription status throughout the application.
   */
  readonly expiredSubscription$: BehaviorSubject<CurrentSubscription | undefined> = new BehaviorSubject<
    CurrentSubscription | undefined
  >(undefined);
  private readonly navDataService: NavDataService = inject(NavDataService);
  private readonly rolesService: RolesService = inject(RolesService);
  private readonly crispChatService: CrispChatService = inject(CrispChatService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly viewService: ViewService = inject(ViewService);
  private readonly authSessionService: AuthSessionService = inject(AuthSessionService);
  private readonly modalManagerService: ModalManagerService = inject(ModalManagerService);
  private readonly commerceService: CommerceService = inject(CommerceService);
  private readonly subscriptionPlansService: SubscriptionPlansService = inject(SubscriptionPlansService);

  constructor() {
    this.authSessionService.currentUserProfile$.subscribe((profile) => {
      if (!profile) {
        this.currentSubscription$.next(undefined);
        this.expiredSubscription$.next(undefined);
        return;
      }
      this.loadCurrentSubscription();
      // this.getExpiredLicense().subscribe();
    });
  }

  /**
   * Try to renew the user membership with membership_expired role.
   * Else if user (was not a candidate and is expired more than 2 years) or already have membership: open crisp chat.
   * Else fetch latest expired license and try to renew with extention variation.
   * Else if no license check previous role and renew assistant or instructor extention variation if he is a candidate
   * Else if the user doesn't have previous role: open crisp chat
   * Fallback to open membership modal - none of above is met "normal expired user from less than 2 years"
   * @returns Observable of data or null depends on the path done
   */
  tryToRenew(): Observable<any> {
    // WEB-5480: disable in production
    const renewMessage = this.translateService.instant('I would like to upgrade my membership');

    // Fetch user expired licenses.
    return this.getExpiredLicense('course_payment_extension').pipe(
      mergeMap((licenses) => {
        const expiredLicense = licenses[0];
        if (expiredLicense) {
          return this.addExtensionToCartAndPay(expiredLicense?.ext_variation_id);
        }

        const prevRole = this.authSessionService.currentUserProfile?.previous_role?.id.toString() ?? '';
        const wasCandidate = this.rolesService.checkDefinedRoles(
          ['assistant_instructor_candidate', 'instructor_candidate'],
          [prevRole]
        );
        const notCandidateExpired2Years = !wasCandidate && this.rolesService.isExpiredMore2years;
        // WEB-3000: check expiration date or fetch d7 expiry
        if (notCandidateExpired2Years || this.rolesService.userHasRole(['basic', 'plus', 'premium'])) {
          this.crispChatService.openChat(renewMessage);
          return of();
        }

        if (
          // If no license found on the backend, check previous role field
          wasCandidate
        ) {
          // Fetch extention variation
          return this.viewService
            .get('/api/v1.1/store_product_variations.json', { args: ['all', 'course_payment_extension'] })
            .pipe(
              mergeMap((data: ProductVariation[]) => {
                const extSku = prevRole === 'instructor_candidate' ? 'ITC_EXT' : 'ATC_EXT';
                const productVarExt = data.find((p) => p.sku === extSku);
                return this.addExtensionToCartAndPay(productVarExt?.variation_id);
              })
            );
        } else if (!prevRole) {
          this.crispChatService.openChat(renewMessage);
          return of();
        } else {
          return this.addExtensionToCartAndPay();
        }
      })
    );
  }

  /**
   * Get expired licence of user from the API.
   *
   * @returns
   *  Observable of the expired licence.
   */
  getExpiredLicense(variationType?: string): Observable<License[]> {
    const options: ViewOptions = {
      args: [variationType || ''],
    };
    return this.viewService.get('/api/v1/my_expired_license.json', options).pipe(
      tap((license) => {
        if (license.length) {
          this.expiredSubscription$.next(
            license?.length
              ? {
                  product_id: license[0].product_id.toString(),
                  variation_id: license[0].variation_id.toString(),
                }
              : undefined
          );
        }
      })
    );
  }

  /**
   * Loads the current subscription for the logged-in user.
   *
   * This method checks if the user is logged in and if their profile is available.
   * If both conditions are met, it fetches the user's active subscription data
   * from the server and updates the `currentSubscription$` observable with the
   * subscription details (product ID and variation ID).
   *
   * If no subscription data is found, the observable is not updated.
   *
   * @returns {void} This method does not return a value.
   */
  loadCurrentSubscription(): void {
    if (
      !this.authSessionService.isLogged ||
      !this.authSessionService.currentUserProfile ||
      this.rolesService.isExpired ||
      this.rolesService.userHasRole('kiter_free')
    ) {
      this.currentSubscription$.next(undefined);
      return;
    }

    this.viewService
      .get('/api/v1/user-active-subscription.json', {
        args: [this.authSessionService.currentUserProfile.uid.toString()],
      })
      .subscribe({
        next: (data) => {
          if (data.length) {
            this.currentSubscription$.next({
              product_id: data[0].product_id,
              variation_id: data[0].variation_id,
            });
            return;
          }
          this.subscriptionPlansService.get().subscribe((plans) => {
            if (!plans.length) {
              return;
            }
            if (plans.length === 1) {
              this.currentSubscription$.next({
                product_id: plans[0].product_id.toString(),
                variation_id: plans[0].variation_id.toString(),
              });
            }
            const keyWordRole = this.authSessionService.currentUserProfile?.current_role.id
              .toString()
              .match(/(basic|plus|premium)/i)?.[0]
              .toLowerCase();
            if (!keyWordRole) {
              this.currentSubscription$.next({
                product_id: plans[0].product_id.toString(),
                variation_id: plans[0].variation_id.toString(),
              });
              return;
            }
            for (const plan of plans) {
              if (plan.field_membership_plan.toLocaleLowerCase().indexOf(keyWordRole) !== -1) {
                this.currentSubscription$.next({
                  product_id: plan.product_id.toString(),
                  variation_id: plan.variation_id.toString(),
                });
                break;
              }
            }
            this.currentSubscription$.next({
              product_id: plans[0].product_id.toString(),
              variation_id: plans[0].variation_id.toString(),
            });
          });
        },
        error: () => {},
      });
  }

  /**
   * Add membership extention variation to cart or open renew modal
   * @param id product variaion id
   * @returns Observable of cart add status "cart order item"
   */
  private addExtensionToCartAndPay(id?: number): Observable<any> {
    // If no license, no previous "atc/itc" candidate role or the backend doesn't have one
    if (!id) {
      return this.modalManagerService.toggle();
    }
    const requestData: CartProductAdd[] = [
      {
        purchased_entity_type: 'commerce_product_variation',
        purchased_entity_id: id,
        quantity: 1,
      },
    ];
    return this.commerceService.addToCart(requestData).pipe(tap(() => this.navDataService.navigate('/shop/checkout')));
  }
}
